import { useState, useEffect, useRef } from "react"
import { Link, useNavigate } from "react-router-dom"
import swal from "sweetalert"
import Loader from "../Common/Loader"
import { useDispatch, useSelector } from "react-redux"
import { getScriptListAction, deleteScriptAction } from "../../../store/actions/ScriptAction"

const Script = ({ diseaseIds = [] }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const scriptList = useSelector((state) => state.script.scripts);
  const numRows = useSelector((state) => state.script.numRows || 0);
  let searchTimeout = null;
  const isFirstLoad = useRef(true);

  const [loading, setLoading] = useState(false)
  const [searchQueries, setSearchQueries] = useState({
    script_text: "",
    diseases: "",
  })
  const [error, setError] = useState("");

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  const [sortField, setSortField] = useState("script_id")
  const [sortOrder, setSortOrder] = useState("asc")
  const [currentPage, setCurrentPage] = useState(0)
  const rowsPerPage = 10

  const fetchScriptFromServer = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      // ✅ Show loader only on first page load
      if (isFirstLoad.current) {
        setLoading(true);
      }

      await dispatch(getScriptListAction(pagination, pageid, search_by, search_keyword, sort_by, sort_order));
    } catch (err) {
      console.error("Failed to fetch script list:", err);
      setError("Failed to load scripts.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false; // ✅ stop loader forever
    }
  };

  useEffect(() => {
    fetchScriptFromServer();
  }, [dispatch]);

  // ================= SEARCH =================
  const handleSearch = (e, field) => {
    const keyword = e.target.value;

    const clearedQueries = {
      script_text: "",
      diseases: "",
    };

    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);

    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);

    // ✅ Debounce API call
    if (searchTimeout) clearTimeout(searchTimeout);

    searchTimeout = setTimeout(() => {
      const searchFieldMap = {
        script_text: "script_text",
        diseases: "disease_name"
      }
      const backendField = searchFieldMap[field] || field;
      
      fetchScriptFromServer(
        keyword.trim() ? backendField : "",
        keyword.trim() ? keyword : "",
        true,
        0,
        sortField,
        sortOrder
      );
    }, 400); // 400ms delay
  };

  // ================= SORT =================
  const sortFieldMap = {
    diseases: "disease_name",
    script_text: "script_text",
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    const backendField = sortFieldMap[field] || field;
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);

    fetchScriptFromServer(
      activeSearchField,
      activeSearchKeyword,
      true,
      0,
      backendField,
      order
    );
  };

  // ================= PAGINATION =================
  const totalPages = Math.ceil(numRows / rowsPerPage);
  const displayTotal = numRows || scriptList.length; // Fallback if numRows is 0
  const startEntry = displayTotal === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endEntry = Math.min((currentPage + 1) * rowsPerPage, displayTotal);

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
      fetchScriptFromServer(
        activeSearchField,
        activeSearchKeyword,
        true,
        page,
        sortField,
        sortOrder
      );
    }
  };

  const handleEdit = (item) => {
    navigate("/add-script", {
      state: {
        item: {
          ...item,
          diseases: item.diseases || [],
        },
        returnTo: {
          currentPage,
          searchQueries,
          sortField,
          sortOrder,
        },
      },
    });
  };

  // ================= DELETE =================
  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this script!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
          await dispatch(deleteScriptAction(id))
          // Refresh the current page after deletion
          await fetchScriptFromServer(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete script");
          console.error("Failed to delete:", err)
        } finally {
          setLoading(false);
        }
      }
    })
  }

  // Server already returns paginated data (no client-side filtering)
  const paginatedData = scriptList;

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link to="/add-script" className="btn btn-primary btn-rounded">
            + Add Script
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th onClick={() => handleSort("script_text")} style={{ cursor: "pointer" }}>
                            Script {sortField === "script_text" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.script_text}
                              onChange={(e) => handleSearch(e, "script_text")}
                              placeholder="Search Script"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("diseases")} style={{ cursor: "pointer" }}>
                            Disease {sortField === "diseases" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.diseases}
                              onChange={(e) => handleSearch(e, "diseases")}
                              placeholder="Search Disease"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((item) => (
                          <tr key={item.script_id}>
                            <td>{item.script_text}</td>
                            <td>
                              {Array.isArray(item.diseases) && item.diseases.length > 0
                                ? item.diseases.map((d) => d.disease_name).join(", ")
                                : "-"}
                            </td>
                            <td>
                              <div className="d-flex">
                                <button
                                  className="btn btn-link p-0 me-3"
                                  onClick={() => handleEdit(item)}
                                  title="Edit Script"
                                >
                                  <i className="fa fa-pencil-alt text-success fs-18"></i>
                                </button>
                                <button
                                  className="btn btn-link p-0"
                                  onClick={() => handleDelete(item.script_id)}
                                  title="Delete Script"
                                >
                                  <i className="fa fa-trash text-danger fs-18"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {paginatedData.length === 0 && (
                          <tr>
                            <td colSpan="3" className="text-center">
                              No Script found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* PAGINATION */}
                  {(numRows > 0 || paginatedData.length > 0) && (
                    <div
                      className="pagination-wrapper"
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "20px",
                        flexWrap: "wrap",
                        flexDirection: "column",
                      }}
                    >
                      <div>
                        Showing {startEntry} to {endEntry} of {displayTotal} entries
                      </div>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                        <button
                          onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                          disabled={currentPage === 0}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: currentPage === 0 ? "not-allowed" : "pointer",
                            opacity: currentPage === 0 ? 0.5 : 1,
                          }}
                        >
                          &lt;
                        </button>
                        {(() => {
                          const pages = [];
                          const maxPagesToShow = 5;
                          const startPage = Math.max(
                            0,
                            Math.min(currentPage - 2, totalPages - maxPagesToShow)
                          );
                          const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                          if (startPage > 0) {
                            pages.push(
                              <button
                                key={0}
                                onClick={() => handlePageChange(0)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  color: "#000",
                                  cursor: "pointer",
                                }}
                              >
                                1
                              </button>
                            );
                            if (startPage > 1) {
                              pages.push(
                                <span
                                  key="dots1"
                                  style={{ padding: "6px 12px", userSelect: "none" }}
                                >
                                  ...
                                </span>
                              );
                            }
                          }

                          for (let i = startPage; i < endPage; i++) {
                            const isActive = currentPage === i;
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handlePageChange(i)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: isActive ? "#007bff" : "#fff",
                                  color: isActive ? "#fff" : "#000",
                                  cursor: "pointer",
                                }}
                              >
                                {i + 1}
                              </button>
                            );
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <span
                                  key="dots2"
                                  style={{ padding: "6px 12px", userSelect: "none" }}
                                >
                                  ...
                                </span>
                              );
                            }
                            pages.push(
                              <button
                                key={totalPages - 1}
                                onClick={() => handlePageChange(totalPages - 1)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  color: "#000",
                                  cursor: "pointer",
                                }}
                              >
                                {totalPages}
                              </button>
                            );
                          }

                          return pages;
                        })()}
                        <button
                          onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                          disabled={currentPage === totalPages - 1}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                            opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                          }}
                        >
                          &gt;
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Script;
